import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useBlastCampaign } from "../../context";
import { PAGINATION_DEFAULTS } from "../../constants/blastConstants";

/**
 * Hook for managing database contacts selection
 */
export const useContactsSelection = () => {
  const {
    databaseContacts,
    databaseContactsMeta,
    selectedContactIds,
    databaseSearchTerm,
    databaseCurrentPage,
    isLoadingContacts,
    fetchDatabaseContacts,
    setDatabaseSearchTerm,
    setDatabasePage,
    toggleContactSelection,
    selectAllContacts,
    deselectAllContacts,
    applyDatabaseSelection,
  } = useBlastCampaign();

  const [localSearchTerm, setLocalSearchTerm] = useState(databaseSearchTerm);
  const searchTimeoutRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (localSearchTerm !== databaseSearchTerm) {
        setDatabaseSearchTerm(localSearchTerm);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [localSearchTerm, databaseSearchTerm, setDatabaseSearchTerm]);

  // Fetch contacts when search or page changes
  useEffect(() => {
    fetchDatabaseContacts({
      page: databaseCurrentPage,
      page_size: PAGINATION_DEFAULTS.PAGE_SIZE,
      search: databaseSearchTerm,
    });
  }, [databaseSearchTerm, databaseCurrentPage, fetchDatabaseContacts]);

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((value) => {
    setLocalSearchTerm(value);
  }, []);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback(
    (page) => {
      setDatabasePage(page);
    },
    [setDatabasePage]
  );

  /**
   * Handle contact selection toggle
   */
  const handleToggleContact = useCallback(
    (contactId) => {
      toggleContactSelection(contactId);
    },
    [toggleContactSelection]
  );

  /**
   * Handle select all
   */
  const handleSelectAll = useCallback(() => {
    selectAllContacts();
  }, [selectAllContacts]);

  /**
   * Handle deselect all
   */
  const handleDeselectAll = useCallback(() => {
    deselectAllContacts();
  }, [deselectAllContacts]);

  /**
   * Check if a contact is selected
   */
  const isContactSelected = useCallback(
    (contactId) => {
      return selectedContactIds.includes(contactId);
    },
    [selectedContactIds]
  );

  /**
   * Apply selection and prepare contacts for blast
   */
  const confirmSelection = useCallback(() => {
    if (selectedContactIds.length === 0) {
      return { success: false, error: "No contacts selected" };
    }
    applyDatabaseSelection();
    return { success: true, count: selectedContactIds.length };
  }, [selectedContactIds.length, applyDatabaseSelection]);

  /**
   * Enhanced contacts with selection state
   */
  const enhancedContacts = useMemo(() => {
    return databaseContacts.map((contact) => ({
      ...contact,
      isSelected: selectedContactIds.includes(contact.partyUuid),
      city: contact.address?.addressCity || "",
      state: contact.address?.addressState || "",
    }));
  }, [databaseContacts, selectedContactIds]);

  /**
   * Pagination metadata
   */
  const pagination = useMemo(() => {
    if (!databaseContactsMeta) return null;

    return {
      totalItems: databaseContactsMeta.totalItems || 0,
      totalPages: databaseContactsMeta.totalPages || 0,
      currentPage: databaseContactsMeta.currentPage || 1,
      perPage: databaseContactsMeta.perPage || PAGINATION_DEFAULTS.PAGE_SIZE,
      hasNext: databaseContactsMeta.hasNext || false,
      hasPrevious: databaseContactsMeta.hasPrevious || false,
    };
  }, [databaseContactsMeta]);

  /**
   * Check if all visible contacts are selected
   */
  const allVisibleSelected = useMemo(() => {
    if (!databaseContacts.length) return false;
    return databaseContacts.every((contact) =>
      selectedContactIds.includes(contact.partyUuid)
    );
  }, [databaseContacts, selectedContactIds]);

  /**
   * Check if some (but not all) visible contacts are selected
   */
  const someVisibleSelected = useMemo(() => {
    if (!databaseContacts.length) return false;
    const selectedCount = databaseContacts.filter((contact) =>
      selectedContactIds.includes(contact.partyUuid)
    ).length;
    return selectedCount > 0 && selectedCount < databaseContacts.length;
  }, [databaseContacts, selectedContactIds]);

  return {
    // State
    contacts: enhancedContacts,
    selectedContactIds,
    selectedCount: selectedContactIds.length,
    searchTerm: localSearchTerm,
    currentPage: databaseCurrentPage,
    isLoading: isLoadingContacts,
    pagination,

    // Computed
    allVisibleSelected,
    someVisibleSelected,

    // Actions
    handleSearchChange,
    handlePageChange,
    handleToggleContact,
    handleSelectAll,
    handleDeselectAll,
    isContactSelected,
    confirmSelection,
    refreshContacts: fetchDatabaseContacts,
  };
};

export default useContactsSelection;
